import 'photoswipe/style.css';
import './styles/ids-gallery.css';
import './styles/stories-viewer.css';

import 'lite-youtube-embed/src/lite-yt-embed.css';
import 'lite-youtube-embed';

import './styles/map.css';

import {
  createBiographyIntro,
  createDocumentFolio,
  createFactTable,
  createMapStage,
  createStoryGallery,
  createPhotoSequence,
  createPlaceDossier,
  createSourceNote,
  createSegmentRail,
  createTimeline,
  photoFigure,
  mountMapStages,
  mountSegmentSpy,
  type PhotoSpec,
  type SegmentSpec,
} from './editorial/primitives';
import { mountIdsGallery } from './editorial/ids-gallery';
import { mountContextualStories } from './editorial/contextual-stories';
import { createStoriesViewer, type StoriesGroup, type StorySlideSpec } from './editorial/stories-viewer';
import {
  createStorySlides,
  type StoryPhotoAnchor,
  type StorySlideCopy,
} from './editorial/story-content';

import { photoByKey, publicPhotoPath } from './renderers/archive-act';
import { schoolFilmPublicationGrant } from './data/family';

import {
  createNarrativePhotoFlow,
  type NarrativePhotoPlacement,
} from './editorial/narrative-photo-flow';

import {
  longformChapters as russianLongformChapters,
  type LongformChapter,
  type NarrativeInterlude,
  type NarrativeSection,
} from './content/longform';
import { kyrgyzLongformChapters } from './content/longform.ky';
import { applyDocumentLocale, isKyrgyz, localeText } from './locale';

applyDocumentLocale();

const longformChapters: readonly LongformChapter[] = isKyrgyz
  ? kyrgyzLongformChapters
  : russianLongformChapters;

const segmentPhotos: Partial<Record<string, string>> = {
  '01': 'origin-01',
  '02': 'education-01',
  '03': 'education-09',
  '04': 'state-01',
  '05': 'state-13',
  '06': 'origin-07',
  '07': 'transition-01',
  '08': 'transition-02',
  '09': 'broadcast-01',
  '10': 'diplomacy-01',
  '11': 'diplomacy-03',
  '12': 'family-03',
};

const segments: SegmentSpec[] = longformChapters.map((chapter) => {
  const segment: SegmentSpec = {
    id: `chapter-${chapter.id}`,
    index: chapter.index,
    title: chapter.label,
    era: chapter.era,
  };
  const photoKey = segmentPhotos[chapter.id];
  return photoKey ? { ...segment, photoKey } : segment;
});

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

type FilmCue = { label: string; start: number; note?: string };

type FilmMetadata = {
  eyebrow?: string;
  source: string;
  duration: string;
};

function createArchiveFilm(
  videoId: string,
  start: number,
  title: string,
  caption: string,
  metadata: FilmMetadata,
  cues: FilmCue[],
): HTMLElement {
  const figure = element('figure', 'ep-archive-film');
  const header = element('header', 'ep-archive-film__header');
  header.append(
    element('p', 'ep-eyebrow', metadata.eyebrow ?? localeText('Семейная видеосъёмка', 'Үй-бүлөлүк видео тасма')),
    element('h3', '', title),
  );

  const frame = element('div', 'ep-archive-film__frame');
  const player = document.createElement('lite-youtube');
  player.className = 'ep-archive-film__player';
  player.setAttribute('videoid', videoId);
  player.setAttribute('title', title);
  player.setAttribute('playlabel', localeText(`${title}. Смотреть видео`, `${title}. Видеону көрүү`));
  const playerParams = new URLSearchParams({ rel: '0' });
  if (start > 0) playerParams.set('start', String(start));
  player.setAttribute('params', playerParams.toString());

  const filmMeta = element('span', 'ep-archive-film__meta');
  filmMeta.append(element('span', '', metadata.source), element('span', '', metadata.duration));
  const loadHint = element('span', 'ep-archive-film__load-hint', localeText('Нажмите — видео откроется здесь', 'Басыңыз — видео ушул жерден ачылат'));
  frame.append(player, filmMeta, loadHint);

  const figcaption = element('figcaption', 'ep-archive-film__caption');
  figcaption.append(element('p', '', caption));
  const cueList = element('ul', 'ep-archive-film__cues');
  for (const cue of cues) {
    const item = element('li');
    const link = element('a', '', cue.label);
    link.href = `https://www.youtube.com/watch?v=${videoId}&t=${cue.start}s`;
    link.target = '_blank';
    link.rel = 'noreferrer';
    if (cue.note) link.title = cue.note;
    item.append(link);
    cueList.append(item);
  }
  figcaption.append(cueList);
  figure.append(header, frame, figcaption);
  return figure;
}

const schoolFilmDescriptions: Record<string, { alt: string; text: string }> = localeText(
  {
    'school-film-021': {
      alt: 'Учительница ведёт урок в светлом классе школы имени Аманбека Карыпкулова.',
      text: 'Урок в обновлённом классе, снятый с верхней точки.',
    },
    'school-film-022': {
      alt: 'Дальний план школы имени Аманбека Карыпкулова.',
      text: 'Дальний план школы имени Аманбека Карыпкулова.',
    },
    'school-film-025': {
      alt: 'Светлый школьный холл с растениями после ремонта.',
      text: 'Обновлённое внутреннее пространство действующей школы.',
    },
    'school-film-030': {
      alt: 'Ученики работают за партами в классе школы имени Аманбека Карыпкулова.',
      text: 'Повседневный урок в классе с краеведческим оформлением.',
    },
    'school-film-031': {
      alt: 'Учебный кабинет школы имени Аманбека Карыпкулова.',
      text: 'Учебный кабинет школы имени Аманбека Карыпкулова.',
    },
    'school-film-032': {
      alt: 'Мемориальная композиция с портретом, книгами и изображениями Таласского края.',
      text: 'На стенде — портрет, книги и изображения Таласского края.',
    },
  },
  {
    'school-film-021': {
      alt: 'Мугалим Аманбек Карыпкулов атындагы мектептин жарык классында сабак өтүп жатат.',
      text: 'Жаңыланган класстагы сабак жогору жактан тартылган.',
    },
    'school-film-022': {
      alt: 'Аманбек Карыпкулов атындагы мектептин алыстан көрүнүшү.',
      text: 'Аманбек Карыпкулов атындагы мектептин алыстан көрүнүшү.',
    },
    'school-film-025': {
      alt: 'Оңдоодон кийинки өсүмдүктөр коюлган жарык мектеп холлу.',
      text: 'Иштеп жаткан мектептин жаңыланган ички мейкиндиги.',
    },
    'school-film-030': {
      alt: 'Окуучулар Аманбек Карыпкулов атындагы мектептин классында парталарда иштеп жатышат.',
      text: 'Аймак таануу материалдары менен жасалгаланган класстагы күнүмдүк сабак.',
    },
    'school-film-031': {
      alt: 'Аманбек Карыпкулов атындагы мектептин окуу кабинети.',
      text: 'Аманбек Карыпкулов атындагы мектептин окуу кабинети.',
    },
    'school-film-032': {
      alt: 'Портрет, китептер жана Талас жергесинин сүрөттөрү коюлган эскерүү бурчу.',
      text: 'Стендде портрет, китептер жана Талас жергесинин сүрөттөрү коюлган.',
    },
  },
);

const schoolFilmCaptions = localeText<Record<string, string>>(
  {},
  {
    'school-film-021': '01:40 · Сабак жогору жактан тартылган.',
    'school-film-022': '01:45 · Мектеп жана Кара‑Суу.',
    'school-film-025': '02:00 · Жаңыланган холл.',
    'school-film-030': '02:25 · Окуучулар сабакта.',
    'school-film-031': '02:30 · Класстагы элес.',
    'school-film-032': '02:35 · Эскерүү бурчу.',
  },
);



function createMapChapter(
  id: string,
  map: HTMLElement | null,
  blocks: HTMLElement[],
  tone: 'paper' | 'limestone' | 'sage' | 'blue' | 'ochre' | 'clay' = 'paper',
): HTMLElement {
  const section = element('section', `ep-chapter ep-chapter--mapped ep-chapter--${tone}`);
  section.id = id;
  section.dataset.epChapter = id;
  section.append(...(map ? [map] : []), ...blocks);
  return section;
}


const root = document.querySelector<HTMLElement>('#assembled-biography');
if (!root) throw new Error('Missing assembled biography root');

const intro = createBiographyIntro({
  eyebrow: '1939—2008',
  title: 'Аманбек Карыпкулов',
  lead: localeText(
    'Человек, посвятивший жизнь своему народу. Аманбек Карыпкулов вошёл в историю Кыргызстана как видный учёный, выдающийся государственный и общественный деятель. Его жизненный путь, многолетний труд и богатое наследие служат примером ответственности, преданности Родине и искреннего стремления быть полезным своей стране.',
    'Өмүрүн элине арнаган инсан. Аманбек Карыпкулов Кыргызстандын тарыхына көрүнүктүү окумуштуу, чыгаан мамлекеттик жана коомдук ишмер катары кирди. Анын өмүр жолу, көп жылдык эмгеги жана бай мурасы жоопкерчиликтин, Мекенге берилгендиктин жана өлкөсүнө чын дилден кызмат кылууга умтулуунун үлгүсү.',
  ),
  photo: {
    key: 'intro-01',
    alt: localeText('Аманбек Карыпкулов в официальном костюме на фоне флага Кыргызстана.', 'Аманбек Карыпкулов расмий костюмчан, Кыргызстандын туусунун алдында.'),
    caption: localeText('Аманбек Карыпкулов · семейный архив.', 'Аманбек Карыпкулов · үй-бүлөлүк архив.'),
  },
});
intro.classList.add('ep-intro--plain', 'is-minimal');

const introCopy = intro.querySelector<HTMLElement>('.ep-intro__copy');
if (!introCopy) throw new Error('Missing biography intro copy');
const introActions = element('div', 'ep-intro__actions');
const readNarrativeLink = element('a', 'ep-intro__action ep-intro__action--read');
readNarrativeLink.href = '#chapter-01';
readNarrativeLink.setAttribute('aria-label', localeText('Начать читать биографию как рассказ', 'Өмүр баянды баян катары окуп баштоо'));
readNarrativeLink.innerHTML = '<span class="ep-intro__action-icon" aria-hidden="true"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3v12M5.5 10.5 10 15l4.5-4.5"/></svg></span><span>' + localeText('Читать как рассказ', 'Баян катары окуу') + '</span>';
introActions.append(readNarrativeLink);
introCopy.append(introActions);

type ChapterTone = 'paper' | 'limestone' | 'sage' | 'blue' | 'ochre' | 'clay';

const chapterTones: Record<string, ChapterTone> = {
  '01': 'limestone',
  '02': 'blue',
  '03': 'sage',
  '04': 'paper',
  '05': 'ochre',
  '06': 'blue',
  '07': 'clay',
  '08': 'sage',
  '09': 'ochre',
  '10': 'blue',
  '11': 'clay',
  '12': 'limestone',
};

const narrativePhotoCaptions: Readonly<Record<string, string>> = localeText(
  {
    'origin-01': 'Аманбек с родителями · 1945',
    'origin-02': 'Волейбол учеников таласской школы · середина 1950-х',
    'origin-03': 'С двоюродным братом Дүйшөном и другом Аманом · 1954',
    'origin-04': 'Выпускной класс средней школы № 1 в Таласе · 1956',
    'origin-05': 'Ученик сельского ПТУ № 6 в Таласе · 1956',
    'origin-06': 'Отец — Карыпкул Орозалиев',
    'origin-07': 'Аманбек Карыпкулов в рабочем кабинете',
    'origin-08': 'Мать — Рысбү Исмаилова',
    'education-01': 'Студент Пржевальского педагогического института · 1957',
    'education-02': 'Студенческие годы, с друзьями · 1958',
    'education-03': 'С будущей женой Кларой Джидеевой · 1959',
    'education-04': 'Летний отдых на Иссык-Куле · 1959',
    'education-05': 'Ленинский стипендиат · 1959',
    'education-06': 'На партийном собрании · 1960',
    'education-07': 'Педагогическая практика в Пржевальске · 1961',
    'education-08': 'Аспиранты Аманбек и Клара · 1964',
    'education-09': 'Аспирант философского факультета КГУ, Фрунзе · 1964',
    'state-01': 'Сотрудник Центрального комитета · 1968',
    'state-02': 'Перед официальной встречей · 1972',
    'state-03': 'С писателями, поэтами и учёными · 1973',
    'state-04': 'С академиком П. Н. Федосеевым · 1974',
    'state-05': 'С президентом Академии наук СССР А. П. Александровым · 1974',
    'state-06': 'На собрании работников образования · 1975',
    'state-07': 'С Т. Усубалиевым во время рабочей поездки · 1978',
    'state-08': 'На республиканском субботнике · 1979',
    'state-09': 'Делегаты XVII съезда Компартии Кыргызстана · 1981',
    'state-10': 'На всесоюзном совещании работников здравоохранения, Москва · 1981',
    'state-11': 'С Аалы Токомбаевым на первомайском параде · 1984',
    'state-12': 'Во время рабочей поездки в Монголию · 1984',
    'state-13': 'Визит премьер-министра Индии Раджива Ганди · 1985',
    'transition-01': 'Подготовка тысячелетия эпоса «Манас» в Таласе · 1995',
    'transition-02': 'Награда за вклад в организацию «Манас-1000»',
    'manas-event-01': 'Юбилейные торжества «Манас-1000» в Таласе · 1995',
    'manas-event-02': 'Флаг Кыргызстана на юбилейной площадке «Манас-1000» · Талас, 1995',
    'manas-event-03': 'Участники праздничной программы «Манас-1000» · Талас, 1995',
    'culture-01': 'Нооруз на площади Ала-Тоо · 1998',
    'culture-02': 'С академиком А. Александровым · 1998',
    'culture-03': 'На 75-летии маэстро А. Жумахматова · 1998',
    'culture-04': 'С Т. Усубалиевым · 1998',
    'culture-05': 'На встрече Чынгыза Айтматова с учёными · 1998',
    'culture-06': 'Вручение Государственной премии в области науки и техники · 1999',
    'broadcast-01': 'Встреча делегации Ирана в ГТРК · 1998',
    'broadcast-02': 'Чынгыз Айтматов на Кыргызском телевидении · 1998',
    'broadcast-03': 'В рабочем кабинете ГТРК · 1999',
    'broadcast-04': 'Подписание соглашения с делегацией КНР · 1999',
    'diplomacy-01': 'С Чынгызом Айтматовым, Аскаром Акаевым и Ишенбаем Токоевым · 2002',
    'diplomacy-02': 'Во время международных переговоров · 2002',
    'diplomacy-03': 'С министром иностранных дел Аскаром Айтматовым · 2003',
    'diplomacy-04': 'На переговорах с Реджепом Тайипом Эрдоганом, Анкара · 2003',
    'diplomacy-05': 'В Стамбуле · 2003',
    'diplomacy-06': 'Вручение верительных грамот Ахмету Недждету Сезеру, Анкара · 2003',
    'diplomacy-07': 'С Сулейманом Демирелем · 2003',
    'diplomacy-08': 'С Реджепом Тайипом Эрдоганом, Анкара · 2004',
    'family-01': 'Семья Аманбека Карыпкулова',
    'family-02': 'Семейная встреча',
    'family-03': 'Аманбек и Клара на мероприятии',
    'family-04': 'Аманбек с Кларой, сыном Нурланом и внуком Семетеем',
    'family-05': 'Клара Джидеева',
    'family-06': 'Правнуки Аманбека Карыпкулова',
    'family-08': 'Аманбек Карыпкулов с семьёй',
    'family-09': 'С сыном Нурланом',
  },
  {
    'origin-01': 'Аманбек ата-энеси менен · 1945',
    'origin-02': 'Таластагы мектеп окуучулары волейбол ойноп жатышат · 1950-жылдардын ортосу',
    'origin-03': 'Бөлөсү Дүйшөн жана досу Аман менен · 1954',
    'origin-04': 'Таластагы № 1 орто мектептин бүтүрүүчү классы · 1956',
    'origin-05': 'Таластагы № 6 айылдык кесиптик-техникалык окуу жайдын окуучусу · 1956',
    'origin-06': 'Атасы — Карыпкул Орозалиев',
    'origin-07': 'Аманбек Карыпкулов иш бөлмөсүндө',
    'origin-08': 'Апасы — Рысбү Исмаилова',
    'education-01': 'Пржевальск педагогикалык институтунун студенти · 1957',
    'education-02': 'Студенттик жылдар, достору менен · 1958',
    'education-03': 'Болочок жубайы Клара Джидеева менен · 1959',
    'education-04': 'Ысык-Көлдөгү жайкы эс алуу · 1959',
    'education-05': 'Лениндик стипендиянын ээси · 1959',
    'education-06': 'Партиялык жыйында · 1960',
    'education-07': 'Пржевальскидеги педагогикалык практика · 1961',
    'education-08': 'Аспиранттар Аманбек жана Клара · 1964',
    'education-09': 'КМУнун философия факультетинин аспиранты, Фрунзе · 1964',
    'state-01': 'Борбордук Комитеттин кызматкери · 1968',
    'state-02': 'Расмий жолугушуунун алдында · 1972',
    'state-03': 'Жазуучулар, акындар жана окумуштуулар менен · 1973',
    'state-04': 'Академик П. Н. Федосеев менен · 1974',
    'state-05': 'СССР Илимдер академиясынын президенти А. П. Александров менен · 1974',
    'state-06': 'Билим берүү кызматкерлеринин жыйынында · 1975',
    'state-07': 'Т. Усубалиев менен иш сапарда · 1978',
    'state-08': 'Республикалык ишембиликте · 1979',
    'state-09': 'Кыргызстан Компартиясынын XVII съездинин делегаттары · 1981',
    'state-10': 'Саламаттык сактоо кызматкерлеринин бүткүл союздук кеңешмесинде, Москва · 1981',
    'state-11': 'Биринчи май парадында Аалы Токомбаев менен · 1984',
    'state-12': 'Монголиядагы иш сапарда · 1984',
    'state-13': 'Индиянын премьер-министри Раджив Гандинин сапары · 1985',
    'transition-01': 'Таласта «Манас» эпосунун миң жылдыгына даярдык · 1995',
    'transition-02': '«Манас-1000» программасын уюштурууга кошкон салымы үчүн сыйлык',
    'manas-event-01': 'Таластагы «Манас-1000» мааракелик салтанаты · 1995',
    'manas-event-02': '«Манас-1000» мааракесинин аянтындагы Кыргызстандын желеги · Талас, 1995',
    'manas-event-03': '«Манас-1000» майрамдык программасынын катышуучулары · Талас, 1995',
    'culture-01': 'Ала-Тоо аянтындагы Нооруз · 1998',
    'culture-02': 'Академик А. Александров менен · 1998',
    'culture-03': 'Маэстро А. Жумахматовдун 75 жылдык мааракесинде · 1998',
    'culture-04': 'Т. Усубалиев менен · 1998',
    'culture-05': 'Чыңгыз Айтматовдун окумуштуулар менен жолугушуусунда · 1998',
    'culture-06': 'Илим жана техника жаатындагы Мамлекеттик сыйлыкты тапшыруу · 1999',
    'broadcast-01': 'МТРКда Иран делегациясы менен жолугушуу · 1998',
    'broadcast-02': 'Чыңгыз Айтматов Кыргыз телекөрсөтүүсүндө · 1998',
    'broadcast-03': 'МТРКнын иш бөлмөсүндө · 1999',
    'broadcast-04': 'КЭР делегациясы менен келишимге кол коюу · 1999',
    'diplomacy-01': 'Чыңгыз Айтматов, Аскар Акаев жана Ишенбай Токоев менен · 2002',
    'diplomacy-02': 'Эл аралык сүйлөшүүлөр учурунда · 2002',
    'diplomacy-03': 'Тышкы иштер министри Аскар Айтматов менен · 2003',
    'diplomacy-04': 'Режеп Тайип Эрдоган менен сүйлөшүүдө, Анкара · 2003',
    'diplomacy-05': 'Стамбулда · 2003',
    'diplomacy-06': 'Ахмет Неждет Сезерге ишеним грамоталарын тапшыруу, Анкара · 2003',
    'diplomacy-07': 'Сүлейман Демирел менен · 2003',
    'diplomacy-08': 'Режеп Тайип Эрдоган менен, Анкара · 2004',
    'family-01': 'Аманбек Карыпкуловдун үй-бүлөсү',
    'family-02': 'Үй-бүлөлүк жолугушуу',
    'family-03': 'Аманбек менен Клара иш-чарада',
    'family-04': 'Аманбек Клара, уулу Нурлан жана небереси Семетей менен',
    'family-05': 'Клара Джидеева',
    'family-06': 'Аманбек Карыпкуловдун чөбөрөлөрү',
    'family-08': 'Аманбек Карыпкулов үй-бүлөсү менен',
    'family-09': 'Уулу Нурлан менен',
  },
);

type SectionPhotoAnchor = readonly [key: string, paragraphIndex: number];

const sectionPhotoAnchors: Readonly<Record<string, readonly SectionPhotoAnchor[]>> = {
  '01:0': [
    ['origin-01', 0],
    ['origin-06', 1],
    ['origin-08', 2],
  ],
  '01:1': [
    ['origin-02', 0],
    ['origin-03', 0],
    ['origin-04', 1],
  ],
  '01:2': [['origin-05', 0]],
  '02:0': [
    ['education-01', 0],
    ['education-02', 0],
    ['education-04', 0],
    ['education-03', 1],
    ['education-05', 0],
    ['education-06', 0],
    ['education-07', 3],
  ],
  '03:0': [
    ['education-09', 1],
    ['education-08', 1],
  ],
  '04:0': [
    ['state-01', 0],
    ['state-02', 1],
    ['state-03', 1],
    ['state-04', 1],
    ['state-05', 1],
    ['state-06', 1],
    ['state-07', 1],
    ['state-08', 1],
    ['state-09', 1],
    ['state-10', 1],
  ],
  '04:1': [
    ['state-11', 0],
    ['state-12', 0],
  ],
  '05:0': [['state-13', 2]],
  '07:0': [['transition-01', 3]],
  '09:0': [
    ['broadcast-01', 0],
    ['broadcast-02', 0],
    ['broadcast-03', 0],
    ['broadcast-04', 1],
    ['culture-01', 1],
    ['culture-02', 1],
    ['culture-03', 2],
    ['culture-04', 2],
  ],
  '09:1': [
    ['culture-05', 0],
    ['culture-06', 1],
  ],
  '10:0': [
    ['diplomacy-01', 0],
    ['diplomacy-02', 0],
  ],
  '11:0': [
    ['diplomacy-03', 0],
    ['diplomacy-04', 2],
    ['diplomacy-06', 2],
    ['diplomacy-07', 2],
    ['diplomacy-05', 3],
    ['diplomacy-08', 3],
  ],
  '12:0': [
    ['family-05', 0],
    ['family-03', 2],
    ['family-02', 2],
  ],
  '12:2': [
    ['school-film-022', 1],
    ['school-film-031', 2],
  ],
  '12:3': [
    ['family-06', 0],
    ['family-08', 0],
    ['family-04', 0],
    ['family-01', 0],
    ['family-09', 0],
  ],
};

const storyOnlyPhotoAnchors: Readonly<Record<string, readonly SectionPhotoAnchor[]>> = {
  '08:0': [
    ['manas-event-01', 1],
    ['manas-event-02', 2],
    ['manas-event-03', 2],
    ['transition-02', 0],
  ],
  '12:1': [
    ['school-film-021', 0],
    ['school-film-025', 0],
    ['school-film-030', 0],
    ['school-film-032', 0],
  ],
};

const interludePhotoAnchors: Readonly<Record<string, readonly SectionPhotoAnchor[]>> = {
  'interlude-1985-1992:0': [['origin-07', 0]],
};

const endingPhotoKeys = ['family-09'] as const;

function storyPhoto(key: string): PhotoSpec {
  const record = photoByKey.get(key);
  if (!record) throw new Error(`Missing publishable story photograph: ${key}`);
  const schoolFilmDescription = schoolFilmDescriptions[key];
  const caption = narrativePhotoCaptions[key] ?? schoolFilmDescription?.text ?? record.t;
  return { key, alt: schoolFilmDescription?.alt ?? caption, caption };
}


function storyPhotoPlacement([key, paragraphIndex]: SectionPhotoAnchor): NarrativePhotoPlacement {
  return { photo: storyPhoto(key), paragraphIndex };
}

function createBiographyPrologue(): HTMLElement {
  const section = element('section', 'ep-prologue');
  section.id = 'biography-prologue';

  const header = element('header', 'ep-prologue__header');
  header.append(
    element('p', 'ep-eyebrow', localeText('Введение', 'Кириш сөз')),
    element('h2', '', localeText('Сохранить память', 'Элести сактоо')),
  );

  const source = element('blockquote', 'ep-prologue__source');
  const sourceParagraphs = localeText(
    [
      'Этот сайт создан, чтобы сохранить память об Аманбеке Карыпкулове, рассказать о его жизни, деятельности и человеческих качествах, показать его вклад в развитие Кыргызстана и передать новым поколениям пример служения народу и Родине.',
    ],
    [
      'Бул сайт Аманбек Карыпкуловдун жаркын элесин сактоо, анын өмүрү, ишмердиги жана адамдык сапаттары жөнүндө айтып берүү, Кыргызстандын өнүгүшүнө кошкон салымын көрсөтүү жана эл менен Мекенге кызмат кылуунун үлгүсүн кийинки муундарга жеткирүү үчүн түзүлгөн.',
    ],
  );
  for (const paragraph of sourceParagraphs) source.append(element('p', '', paragraph));


  const copy = element('div', 'ep-prologue__copy');
  copy.append(source);

  const photos = element('div', 'ep-prologue__photos');
  const parents = photoFigure(storyPhoto('origin-01'), 'ep-prologue__photo');
  const parentsOpener = parents.querySelector<HTMLElement>('[data-ids-gallery-open]');
  const parentsImage = parents.querySelector<HTMLImageElement>('img');
  const parentsCaption = parents.querySelector<HTMLElement>('figcaption span');
  if (parentsOpener) parentsOpener.dataset.idsNarrativeTarget = 'chapter-01';
  if (parentsImage) parentsImage.sizes = '(max-width: 700px) 100vw, 32vw';
  if (parentsCaption) parentsCaption.textContent = parentsCaption.textContent.replace(/\.\s*$/, '');

  const careerPair = element('figure', 'ep-prologue__career-pair');
  const careerMedia = element('div', 'ep-prologue__career-media');
  const careerCaption = element('figcaption', 'ep-prologue__career-caption');
  const careerSpecs = [
    { key: 'broadcast-03', target: 'chapter-09' },
    { key: 'diplomacy-03', target: 'chapter-11' },
  ] as const;
  for (const { key, target } of careerSpecs) {
    const sourceFigure = photoFigure(storyPhoto(key));
    const opener = sourceFigure.querySelector<HTMLElement>('[data-ids-gallery-open]');
    const image = sourceFigure.querySelector<HTMLImageElement>('img');
    const sourceCaption = sourceFigure.querySelector('figcaption');
    const captionText = sourceCaption?.textContent?.trim().replace(/\.\s*$/, '') ?? '';
    sourceCaption?.remove();
    if (opener) opener.dataset.idsNarrativeTarget = target;
    if (image) image.sizes = '(max-width: 700px) 50vw, 27vw';

    const panel = element('div', 'ep-prologue__career-panel');
    panel.dataset.photoKey = key;
    while (sourceFigure.firstChild) panel.append(sourceFigure.firstChild);
    careerMedia.append(panel);
    careerCaption.append(element('span', 'ep-prologue__career-caption-item', captionText));
  }
  careerPair.append(careerMedia, careerCaption);
  photos.append(parents, careerPair);

  section.append(header, copy, photos);
  return section;
}


const editorialPhotoKeys = Object.values(storyOnlyPhotoAnchors).flat().map(([key]) => key);

const narrativePhotoKeys = [
  'intro-01',
  ...Object.values(sectionPhotoAnchors).flat().map(([key]) => key),
  ...Object.values(interludePhotoAnchors).flat().map(([key]) => key),
  ...editorialPhotoKeys,
];
const duplicateNarrativePhotoKeys = narrativePhotoKeys.filter(
  (key, index) => narrativePhotoKeys.indexOf(key) !== index,
);
const mappedNarrativePhotoKeys = [
  ...narrativePhotoKeys,
  ...Object.values(segmentPhotos).filter((key): key is string => key !== undefined),
];
const narrativePhotoKeySet = new Set(mappedNarrativePhotoKeys);
const unmappedNarrativePhotoKeys = [...photoByKey.keys()].filter((key) => !narrativePhotoKeySet.has(key));
const unknownNarrativePhotoKeys = mappedNarrativePhotoKeys.filter((key) => !photoByKey.has(key));
if (
  duplicateNarrativePhotoKeys.length
  || unmappedNarrativePhotoKeys.length
  || unknownNarrativePhotoKeys.length
) {
  throw new Error(
    `Invalid visual-story photo map · duplicate: ${duplicateNarrativePhotoKeys.join(', ') || 'none'}`
    + ` · unmapped: ${unmappedNarrativePhotoKeys.join(', ') || 'none'}`
    + ` · unknown: ${unknownNarrativePhotoKeys.join(', ') || 'none'}`,
  );
}


function createLongformTextSection(sectionSpec: NarrativeSection, sectionIndex: number): HTMLElement {
  const section = element('section', 'ep-longform-section');
  if (sectionIndex % 2 === 1) section.classList.add('is-reverse');

  const header = element('header', 'ep-longform-section__header');
  if (sectionSpec.eyebrow) header.append(element('p', 'ep-eyebrow', sectionSpec.eyebrow));
  header.append(element('h3', '', sectionSpec.title));
  if (sectionSpec.lead) header.append(element('p', 'ep-longform-section__lead', sectionSpec.lead));

  const copy = element('div', 'ep-longform-section__copy');
  for (const paragraph of sectionSpec.paragraphs) copy.append(element('p', '', paragraph));
  section.append(header, copy);
  return section;
}

function createNarrativeBlock(
  chapter: LongformChapter,
  sectionSpec: NarrativeSection,
  sectionIndex: number,
): HTMLElement {
  const isOpening = sectionIndex === 0;
  const presentation: NarrativeSection = isOpening
    ? {
        ...sectionSpec,
        eyebrow: `${chapter.index} · ${chapter.era}`,
        title: chapter.title,
        lead: chapter.dek,
      }
    : sectionSpec;
  const sectionKey = `${chapter.id}:${sectionIndex}`;
  const photoAnchors = sectionPhotoAnchors[sectionKey];
  const block = photoAnchors
    ? createNarrativePhotoFlow({
        eyebrow: presentation.eyebrow ?? chapter.era,
        title: presentation.title,
        lead: presentation.lead ?? chapter.dek,
        paragraphs: [...presentation.paragraphs],
        photos: photoAnchors.map(storyPhotoPlacement),
      })
    : createLongformTextSection(presentation, sectionIndex);
  if (isOpening) block.classList.add('is-chapter-opening');
  return block;
}

function createChronologyInterlude(interludeSpec: NarrativeInterlude): HTMLElement {
  const interlude = element('aside', 'ep-chronology-interlude');
  interlude.id = interludeSpec.id;

  const header = element('header', 'ep-chronology-interlude__header');
  header.append(
    element('p', 'ep-eyebrow', interludeSpec.era),
    element('h2', '', interludeSpec.title),
    element('p', 'ep-chronology-interlude__lead', interludeSpec.dek),
  );

  const body = element('div', 'ep-chronology-interlude__body');
  for (const [sectionIndex, sectionSpec] of interludeSpec.sections.entries()) {
    const photoAnchors = interludePhotoAnchors[`${interludeSpec.id}:${sectionIndex}`];
    const block = photoAnchors
      ? createNarrativePhotoFlow({
          eyebrow: sectionSpec.eyebrow ?? interludeSpec.era,
          title: sectionSpec.title,
          lead: sectionSpec.lead ?? interludeSpec.dek,
          paragraphs: [...sectionSpec.paragraphs],
          photos: photoAnchors.map(storyPhotoPlacement),
        })
      : createLongformTextSection(sectionSpec, sectionIndex);
    body.append(block);
  }

  interlude.append(header, body);
  return interlude;
}


const chapterGallery = createStoryGallery({
  eyebrow: localeText('Оглавление · 1939—2008', 'Мазмун · 1939—2008'),
  title: localeText('Двенадцать глав одной жизни', 'Бир өмүрдүн он эки бөлүмү'),
  variant: 'feature',
  items: longformChapters.map((chapter) => {
    const photoKey = segmentPhotos[chapter.id];
    if (!photoKey) throw new Error(`Missing chapter index photograph: ${chapter.id}`);
    return {
      year: chapter.era,
      title: chapter.label,
      detail: chapter.title,
      photo: storyPhoto(photoKey),
      narrativeTarget: `chapter-${chapter.id}`,
    };
  }),
  defaultIndex: 0,
});

type ManasWebSource = {
  icon: string;
  source: string;
  kind: string;
  title: string;
  note: string;
  href: string;
};

function createManasSourceDesk(): HTMLElement {
  const sources: readonly ManasWebSource[] = localeText(
    [
      {
        icon: './source-icons/un-digital-library.ico',
        source: 'Цифровая библиотека ООН',
        kind: '29 июля 1994',
        title: 'Резолюция о тысячелетии эпоса «Манас»',
        note: 'Экономический и Социальный Совет признал 1995-й годом тысячелетия эпоса. Решение приняли без голосования.',
        href: 'https://digitallibrary.un.org/record/169310',
      },
      {
        icon: './source-icons/unesco.ico',
        source: 'UNESDOC · ЮНЕСКО',
        kind: '28 августа — 1 сентября 1995',
        title: 'Материалы симпозиума «Манас и мировое эпическое наследие»',
        note: 'В одном издании собраны программа симпозиума и доклады исследователей эпоса.',
        href: 'https://unesdoc.unesco.org/ark:/48223/pf0000101544',
      },
      {
        icon: './source-icons/minjust.jpg',
        source: 'Правовая база Минюста КР',
        kind: '24 ноября 1995',
        title: 'Постановление Правительства КР № 510',
        note: 'Итоговый документ перечисляет книги, фильмы, научные встречи, стройки и международных участников.',
        href: 'https://cbd.minjust.gov.kg/7-5653/edition/308310/ru',
      },
      {
        icon: './source-icons/aramcoworld.gif',
        source: 'AramcoWorld',
        kind: 'май–июнь 1996',
        title: 'Фоторепортаж «Manas at 1000»',
        note: 'Фотографии юбилейных мероприятий и рассказ их участника.',
        href: 'https://archive.aramcoworld.com/issue/199603/manas.at.1000-the.rebirth.of.kyrgyzstan.htm',
      },
    ],
    [
      {
        icon: './source-icons/un-digital-library.ico',
        source: 'БУУнун санарип китепканасы',
        kind: '1994-жылдын 29-июлу',
        title: '«Манас» эпосунун миң жылдыгы жөнүндө резолюция',
        note: 'Экономикалык жана Социалдык Кеңеш 1995-жылды эпостун миң жылдыгы деп тааныган. Чечим добуш бербестен кабыл алынган.',
        href: 'https://digitallibrary.un.org/record/169310',
      },
      {
        icon: './source-icons/unesco.ico',
        source: 'UNESDOC · ЮНЕСКО',
        kind: '1995-жылдын 28-августу — 1-сентябры',
        title: '«Манас жана дүйнөлүк эпикалык мурас» симпозиумунун материалдары',
        note: 'Бир басылмага симпозиумдун программасы жана эпос изилдөөчүлөрүнүн докладдары топтолгон.',
        href: 'https://unesdoc.unesco.org/ark:/48223/pf0000101544',
      },
      {
        icon: './source-icons/minjust.jpg',
        source: 'Юстиция министрлигинин укуктук базасы',
        kind: '1995-жылдын 24-ноябры',
        title: 'Өкмөттүн № 510 токтому',
        note: 'Жыйынтыктоочу документте китептер, тасмалар, илимий жолугушуулар, курулуштар жана чет өлкөлүк катышуучулар саналат.',
        href: 'https://cbd.minjust.gov.kg/7-5653/edition/308310/ru',
      },
      {
        icon: './source-icons/aramcoworld.gif',
        source: 'AramcoWorld',
        kind: '1996-жылдын май–июну',
        title: '«Manas at 1000» сүрөттүү репортажы',
        note: 'Мааракелик иш-чаралардын сүрөттөрү жана ага катышкан адамдын баяны.',
        href: 'https://archive.aramcoworld.com/issue/199603/manas.at.1000-the.rebirth.of.kyrgyzstan.htm',
      },
    ],
  );

  const section = element('section', 'ep-manas-source-desk');
  const header = element('header', 'ep-manas-source-desk__header');
  header.append(
    element('p', 'ep-manas-source-desk__mark', '1995'),
    element('p', 'ep-manas-source-desk__eyebrow', localeText('«Манас-1000» · документы', '«Манас-1000» · документтер')),
    element('h3', '', localeText('Четыре документа юбилея', 'Мааракенин төрт документи')),
    element('p', 'ep-manas-source-desk__lead', localeText(
      'Резолюция, программа симпозиума, постановление Правительства и репортаж очевидца — четыре документа 1994–1996 годов.',
      '1994–1996-жылдардагы резолюция, симпозиумдун программасы, Өкмөттүн токтому жана күбөнүн репортажы.',
    )),
  );

  const list = element('ol', 'ep-manas-source-desk__list');
  list.setAttribute('aria-label', localeText('Документы юбилея «Манас-1000»', '«Манас-1000» мааракесинин документтери'));
  for (const [index, source] of sources.entries()) {
    const item = element('li', 'ep-manas-source-desk__entry');
    const link = element('a', 'ep-manas-source-desk__item');
    link.href = source.href;
    link.target = '_blank';
    link.rel = 'noreferrer';
    link.setAttribute('aria-label', localeText(`${source.title}. Открыть источник в новой вкладке`, `${source.title}. Булакты жаңы терезеде ачуу`));

    const reference = element('div', 'ep-manas-source-desk__reference');
    const icon = element('img', 'ep-manas-source-desk__icon') as HTMLImageElement;
    icon.src = source.icon;
    icon.alt = '';
    icon.loading = 'lazy';
    icon.decoding = 'async';
    icon.width = 96;
    icon.height = 64;
    const identity = element('div', 'ep-manas-source-desk__identity');
    identity.append(
      element('strong', 'ep-manas-source-desk__source-name', source.source),
      element('p', 'ep-manas-source-desk__kind', source.kind),
    );
    reference.append(icon, identity);
    const copy = element('div', 'ep-manas-source-desk__copy');
    copy.append(
      element('h4', '', source.title),
      element('p', 'ep-manas-source-desk__note', source.note),
    );
    const action = element('span', 'ep-manas-source-desk__action', '↗');
    action.setAttribute('aria-hidden', 'true');
    link.append(
      element('span', 'ep-manas-source-desk__index', String(index + 1).padStart(2, '0')),
      reference,
      copy,
      action,
    );
    item.append(link);
    list.append(item);
  }

  section.append(header, list);
  return section;
}



function createEditorialRubrics(chapterId: string, sectionIndex: number): HTMLElement[] {
  const sectionKey = `${chapterId}:${sectionIndex}`;

  if (sectionKey === '03:1') {
    return [
      createTimeline(localeText('Учёба и начало научной работы · 1957–1969', 'Окуу жана илимий иштин башталышы · 1957–1969'), [
        { year: '1957', title: 'Пржевальск', detail: localeText('Поступление на факультет русского языка и литературы педагогического института.', 'Педагогикалык институттун орус тили жана адабияты факультетине кабыл алуу.') },
        { year: '1962', title: localeText('Своя аудитория', 'Өз аудиториясы'), detail: localeText('Диплом с отличием и два года преподавания в институте, который он только что окончил.', 'Артыкчылык диплому жана өзү жаңы эле бүтүргөн институттагы эки жылдык окутуучулук иш.') },
        { year: '1964', title: 'Фрунзе', detail: localeText('Аспирантура Киргизского государственного университета по философии.', 'Кыргыз мамлекеттик университетинин философия боюнча аспирантурасы.') },
        { year: '1969', title: localeText('Первая диссертация', 'Биринчи диссертация'), detail: localeText('Защита исследования о росте культурных потребностей сельских жителей Киргизской ССР.', 'Кыргыз ССРиндеги айыл тургундарынын маданий керектөөлөрүнүн өсүшү тууралуу изилдөөнү коргоо.') },
      ]),
    ];
  }

  if (sectionKey === '03:2') {
    return [
      createDocumentFolio({
        mark: '447',
        kicker: localeText('Монография · образование и общество', 'Монография · билим жана коом'),
        title: 'Народное образование в\u00a0развитом социалистическом обществе',
        place: 'Фрунзе',
        year: '1982',
        note: localeText('447 страниц о народном образовании как одном из главных общественных институтов.', 'Элге билим берүүнү негизги коомдук институттардын бири катары караган 447 бет.'),
      }),
    ];
  }

  if (sectionKey === '06:0') {
    const chuyFolio = createDocumentFolio({
      mark: '718',
      kicker: localeText('Региональная энциклопедия · два языка', 'Аймактык энциклопедия · эки тил'),
      title: '«Чүй облусу / Чуйская область»',
      place: localeText('Бишкек', 'Бишкек'),
      year: '1994',
      note: localeText(
        'Библиографическая запись фиксирует 718‑страничное издание на кыргызском и русском языках и связывает его с Аманбеком Карыпкуловым.',
        'Библиографиялык жазма кыргыз жана орус тилдериндеги 718 беттик басылманы көрсөтүп, аны Аманбек Карыпкулов менен байланыштырат.',
      ),
    });
    const chuyCopy = chuyFolio.querySelector<HTMLElement>('.ep-document-folio__copy');
    if (!chuyCopy) throw new Error('Missing Chüy document folio copy');
    const chuySource = element('p', 'ep-document-folio__sources');
    const chuyLink = element('a', '', localeText('Каталог · Open Library', 'Каталог · Open Library'));
    chuyLink.href = 'https://openlibrary.org/works/OL23589261W';
    chuyLink.target = '_blank';
    chuyLink.rel = 'noreferrer';
    chuySource.append(chuyLink);
    chuyCopy.append(chuySource);

    const manasFolio = createDocumentFolio({
      mark: 'II',
      kicker: localeText('Два тома · подлинные обложки', 'Эки том · түп нуска мукабалар'),
      title: '«Манас-энциклопедия»',
      place: localeText('Бишкек', 'Бишкек'),
      year: '1995',
      covers: [
        {
          src: './photos/public/books/manas-encyclopedia-tom-1.jpg',
          alt: localeText('Фотография обложки первого тома «Манас-энциклопедии» 1995 года.', '1995-жылкы «Манас-энциклопедиянын» биринчи томунун мукабасы.'),
          volume: localeText('Том I', 'I том'),
          sourceUrl: 'https://bilim.akipress.org/lib/book:466/manas-entsiklopediya-tom-1-bishkek-1995g/',
          sourceLabel: 'Билим AKIpress',
          width: 222,
          height: 300,
        },
        {
          src: './photos/public/books/manas-encyclopedia-tom-2.jpg',
          alt: localeText('Фотография обложки второго тома «Манас-энциклопедии» 1995 года.', '1995-жылкы «Манас-энциклопедиянын» экинчи томунун мукабасы.'),
          volume: localeText('Том II', 'II том'),
          sourceUrl: 'https://bilim.akipress.org/lib/book:464/manas-entsiklopediya-tom-2-bishkek-1995g/',
          sourceLabel: 'Билим AKIpress',
          width: 218,
          height: 300,
        },
      ],
      note: localeText('В выходных данных Аманбек Карыпкулов и Р. Сарыбеков указаны редакторами. Два тома создавал коллектив авторов и специалистов.', 'Чыгарылыш маалыматында Аманбек Карыпкулов менен Р. Сарыбеков редакторлор катары көрсөтүлгөн. Эки томду авторлор жана адистер жамааты даярдаган.'),
    });
    const manasCopy = manasFolio.querySelector<HTMLElement>('.ep-document-folio__copy');
    if (!manasCopy) throw new Error('Missing Manas document folio copy');
    const manasSources = element('p', 'ep-document-folio__sources');
    for (const [label, href] of [
      [localeText('Цифровой экземпляр · том I', 'Санарип нуска · I том'), 'https://bilim.akipress.org/lib/book:466/manas-entsiklopediya-tom-1-bishkek-1995g/'],
      [localeText('Цифровой экземпляр · том II', 'Санарип нуска · II том'), 'https://bilim.akipress.org/lib/book:464/manas-entsiklopediya-tom-2-bishkek-1995g/'],
    ] as const) {
      const link = element('a', '', label);
      link.href = href;
      link.target = '_blank';
      link.rel = 'noreferrer';
      manasSources.append(link);
    }
    manasCopy.append(manasSources);
    return [chuyFolio, manasFolio];
  }



  if (sectionKey === '08:0') {
    const photoSequence = createPhotoSequence(localeText('«Манас-1000» · Талас · 1995', '«Манас-1000» · Талас · 1995-жыл'), [
      {
        ...storyPhoto('manas-event-01'),
        text: localeText('Главная площадка юбилея в Таласе: конное шествие и зрители на склонах.', 'Таластагы мааракенин башкы аянты: атчандар жүрүшү жана тоо боорундагы көрүүчүлөр.'),
      },
      {
        ...storyPhoto('manas-event-02'),
        text: localeText('Флаг Кыргызстана над участниками праздничной программы «Манас-1000».', '«Манас-1000» майрамдык программасынын катышуучуларынын үстүндөгү Кыргызстандын желеги.'),
      },
      {
        ...storyPhoto('manas-event-03'),
        text: localeText('Массовое театрализованное представление на юбилейной площадке.', 'Мааракелик аянттагы массалык театрлаштырылган оюн-зоок.'),
      },
      {
        ...storyPhoto('transition-02'),
        text: localeText('Награда Аманбеку Карыпкулову за вклад в организацию «Манас-1000».', 'Аманбек Карыпкуловго «Манас-1000» программасын уюштурууга кошкон салымы үчүн берилген сыйлык.'),
      },
    ]);
    photoSequence.classList.add('ep-photo-sequence--manas');
    const photoSequenceTitle = photoSequence.querySelector('h3');
    if (!photoSequenceTitle) throw new Error('Missing Manas photo sequence title');
    photoSequenceTitle.replaceChildren(
      element('span', 'ep-photo-sequence__title-line', '«Манас-1000»'),
      element('span', 'ep-photo-sequence__title-line', localeText('Талас · 1995', 'Талас · 1995-жыл')),
    );
    return [
      photoSequence,
      createFactTable(localeText('Масштаб культурной программы', 'Маданий программанын масштабы'), [
        { label: '«Акыл»', value: localeText('5 издательств', '5 басма'), note: localeText('«Кыргызстан», «Мектеп», «Адабият», «Госкомиздат» и «Ала-Тоо» объединили в государственный концерн в 1994 году.', '1994-жылы «Кыргызстан», «Мектеп», «Адабият», «Госкомиздат» жана «Ала-Тоо» мамлекеттик концернге бириккен.') },
        { label: localeText('Издания', 'Басылмалар'), value: localeText('Более 50', '50дөн ашык'), note: localeText('Книги, брошюры, пособия и буклеты, выпущенные к юбилею «Манас-1000».', '«Манас-1000» мааракесине карата чыккан китептер, брошюралар, окуу куралдары жана буклеттер.') },
        { label: localeText('Экран', 'Экран'), value: localeText('Более 10 фильмов', '10дон ашык тасма'), note: localeText('Кино- и видеоработы, созданные в рамках юбилейной программы.', 'Мааракелик программанын алкагында тартылган кино жана видео тасмалар.') },
        { label: localeText('Симпозиум', 'Симпозиум'), value: localeText('Более 130 учёных', '130дан ашык окумуштуу'), note: localeText('Участники международного форума «Манас и мировое эпическое наследие».', '«Манас жана дүйнөлүк эпикалык мурас» эл аралык форумунун катышуучулары.') },
        { label: localeText('География', 'География'), value: localeText('Более 60 стран', '60тан ашык өлкө'), note: localeText('В мероприятиях юбилейного года участвовали представители более чем 60 стран.', 'Маараке жылындагы иш-чараларга 60тан ашык өлкөнүн өкүлдөрү катышкан.') },
      ]),
      createManasSourceDesk(),
      createArchiveFilm(
        '9jUEkcdOzZ8',
        680,
        localeText('«Манас-1000» в документальном фильме УТРК', 'УТРКнын тасмасындагы «Манас-1000»'),
        localeText(
          'В эпизоде 11:20–13:30 показаны Кыргызская энциклопедия, концерн «Акыл» и подготовка к «Манас-1000». Плеер начинает с этого эпизода; весь фильм доступен по ссылке ниже.',
          '11:20–13:30 аралыгындагы үзүндүдө Кыргыз энциклопедиясы, «Акыл» концерни жана «Манас-1000» мааракесине даярдык көрсөтүлөт. Плеер ушул эпизоддон башталат; тасманы толугу менен төмөнкү шилтемеден көрүүгө болот.',
        ),
        {
          eyebrow: localeText('УТРК · документальный фильм · 2019', 'УТРК · даректүү тасма · 2019'),
          source: '«Демократия сабактары / Аманбек Карыпкулов»',
          duration: localeText('32 минуты · кыргызский', '32 мүнөт · кыргызча'),
        },
        [
          { label: localeText('Смотреть весь фильм с начала', 'Тасманы башынан толук көрүү'), start: 0 },
        ],
      ),
    ];
  }


  if (sectionKey === '09:1') {
    return [
      createDocumentFolio({
        mark: 'КТР',
        kicker: localeText('Книга о пяти годах вещания', 'Телерадиодогу беш жыл тууралуу китеп'),
        title: 'КТР: Испытание временем',
        place: 'Стамбул · Diyalog Avrasya',
        year: '2003',
        note: localeText('Книгу о работе государственной телерадиокорпорации Карыпкулов издал уже после ухода с должности.', 'Карыпкулов мамлекеттик телерадиокорпорациянын иши тууралуу китепти кызматтан кеткенден кийин чыгарган.'),
      }),
    ];
  }

  if (sectionKey === '11:0') {
    return [
      createTimeline(localeText('Три даты дипломатической службы', 'Дипломатиялык кызматтын үч датасы'), [
        { year: '15.07.2002', title: 'Анкара', detail: localeText('Назначение чрезвычайным и полномочным послом Кыргызстана в Турции.', 'Кыргызстандын Түркиядагы атайын жана ыйгарым укуктуу элчиси болуп дайындалышы.') },
        { year: '14.12.2004', title: 'Скопье', detail: localeText('Вручение верительных грамот президенту Бранко Црвенковскому в качестве первого посла Кыргызстана в Македонии.', 'Кыргызстандын Македониядагы биринчи элчиси катары президент Бранко Црвенковскийге ишеним грамоталарын тапшырышы.') },
        { year: '25.04.2005', title: localeText('Возвращение', 'Кайтуу'), detail: localeText('Завершение дипломатической службы и возвращение в Бишкек.', 'Дипломатиялык кызматтын аякташы жана Бишкекке кайтуу.') },
      ]),
    ];
  }

  if (sectionKey === '12:2') {
    const archive = element('details', 'ep-school-archive');
    const summary = element('summary', 'ep-school-archive__summary');
    summary.append(
      element('span', '', localeText('Школьный архив', 'Мектеп архиви')),
      element('strong', '', localeText('Ещё 4 кадра', 'Дагы 4 кадр')),
    );
    const grid = element('div', 'ep-school-archive__grid');
    for (const key of ['school-film-021', 'school-film-025', 'school-film-030', 'school-film-032']) {
      grid.append(photoFigure(storyPhoto(key), 'ep-photo--school-archive'));
    }
    archive.append(summary, grid);
    return [archive];
  }

  if (sectionKey === '12:1') {
    return [
      createSourceNote(
        localeText('15 или 16 октября', '15 же 16-октябрь'),
        localeText('Некролог друзей и коллег, опубликованный по горячим следам, называет 15 октября 2008 года; поздние биографические записи — 16 октября. Гражданская панихида 17 октября подтверждена отдельно.', 'Достору менен кесиптештеринин окуядан көп өтпөй жарыяланган некрологунда 2008-жылдын 15-октябры көрсөтүлгөн; кийинки биографиялык жазмаларда 16-октябрь деп берилет. 17-октябрдагы жарандык коштошуу зыйнаты өзүнчө булак менен ырасталган.'),
        localeText('Некролог 2008 года · правительственные и биографические записи', '2008-жылкы некролог · өкмөттүк жана биографиялык жазмалар'),
      ),
    ];
  }


  return [];
}

function createChapterInterludes(chapterId: string, sectionIndex: number): HTMLElement[] {
  const interludes: HTMLElement[] = createEditorialRubrics(chapterId, sectionIndex);

  if (chapterId === '12' && sectionIndex === 2) {
    interludes.push(
      createArchiveFilm(
        'tustx66OoHM',
        0,
        localeText('Школа после ремонта', 'Оңдоодон кийинки мектеп'),
        localeText('Семейная видеосъёмка показывает действующую школу имени Аманбека Карыпкулова: классы, уроки и мемориальную композицию.', 'Үй-бүлөлүк видео тасма Аманбек Карыпкулов атындагы иштеп жаткан мектепти көрсөтөт: класстар, сабактар жана эскерүү бурчу.'),
        {
          source: localeText('УТРК Талас · семейный видеоархив · 2026', 'УТРК Талас · үй-бүлөлүк видеоархив · 2026'),
          duration: localeText('4:10 · кыргызский', '4:10 · кыргызча'),
        },
        [{ label: localeText('Смотреть сюжет · 4:10', 'Сюжетти көрүү · 4:10'), start: 0 }],
      ),
    );
  }

  return interludes;
}

interface RouteMapPlacement {
  sectionIndex: number;
  beatId: string;
  title: string;
  lead: string;
  routeKind: 'education';
}

const routeMapByChapter: Partial<Record<string, RouteMapPlacement>> = {
  '02': {
    sectionIndex: 0,
    beatId: 'beat-02',
    title: 'Талас · Пржевальск · Фрунзе',
    lead: localeText('В 1957 году Карыпкулов уехал из Таласа учиться на филолога в Пржевальске; в 1964 году поступил в аспирантуру во Фрунзе.', '1957‑жылы Карыпкулов Таластан Пржевальскиге филолог адистигине окууга кетип, 1964‑жылы Фрунзеде аспирантурага өттү.'),
    routeKind: 'education',
  },
};

function createRouteMap(chapter: LongformChapter, placement: RouteMapPlacement): HTMLElement {
  const map = createMapStage({
    index: chapter.index,
    era: localeText('Места', 'Жерлер'),
    title: placement.title,
    lead: placement.lead,
    beatId: placement.beatId,
    placeLabel: '',
  });
  map.classList.add('ep-map-stage--route');
  map.dataset.routeKind = placement.routeKind;
  if (chapter.id === '02') {
    const h2 = map.querySelector<HTMLElement>('h2');
    if (h2) {
      const cities = placement.title.split(' · ');
      const years = ['1956', '1957', '1964'];
      h2.replaceChildren(...cities.flatMap((city, index) => {
        const year = document.createElement('span');
        year.className = 'ep-map-route-year';
        year.textContent = ` · ${years[index]}`;
        const parts: (string | HTMLElement)[] = [city, year];
        if (index < cities.length - 1) {
          const sep = document.createElement('span');
          sep.className = 'ep-map-route-sep';
          sep.textContent = ' · ';
          parts.push(' ', sep);
        }
        return parts;
      }));
    }
  }
  return map;
}

function createLongformChapter(chapter: LongformChapter): HTMLElement {
  const blocks: HTMLElement[] = [];
  const routeMap = routeMapByChapter[chapter.id];
  for (const [sectionIndex, sectionSpec] of chapter.sections.entries()) {
    blocks.push(createNarrativeBlock(chapter, sectionSpec, sectionIndex));
    if (routeMap?.sectionIndex === sectionIndex) blocks.push(createRouteMap(chapter, routeMap));
    blocks.push(...createChapterInterludes(chapter.id, sectionIndex));
  }

  return createMapChapter(
    `chapter-${chapter.id}`,
    null,
    blocks,
    chapterTones[chapter.id] ?? 'paper',
  );
}

const pageChapters = longformChapters.flatMap((chapter) => [
  createLongformChapter(chapter),
  ...(chapter.interludeAfter ? [createChronologyInterlude(chapter.interludeAfter)] : []),
]);

const biographyPrologue = createBiographyPrologue();

const ending = element('footer', 'ep-end ep-end--photo');
const endingCopy = element('div', 'ep-end__copy');
const endingQuote = element('figure', 'ep-end__quote');
endingQuote.append(
  element(
    'blockquote',
    '',
    localeText(
      '«Аманбек Карыпкулов прожил яркую, интересную жизнь. Он всегда занимал высокие и ответственные посты и всегда блестяще справлялся с порученной ему работой. Мы никогда не забудем, какой неоценимый вклад внес этот великий человек в развитие народного образования, науки, государственного управления и дипломатии страны».',
      '«Аманбек Карыпкулов жаркын, кызыктуу өмүр сүрдү. Ал дайыма жогорку жана жооптуу кызматтарды аркалап, тапшырылган ишти мыкты аткарчу. Бул улуу адамдын эл агартууга, илимге, мамлекеттик башкарууга жана өлкөнүн дипломатиясына кошкон баа жеткис салымын биз эч качан унутпайбыз».',
    ),
  ),
);
const endingQuoteSource = element('figcaption');
endingQuoteSource.append(
  element(
    'strong',
    '',
    localeText(
      'Искендербек Айдаралиев, первый вице-премьер-министр Кыргызстана',
      'Искендербек Айдаралиев, Кыргызстандын биринчи вице-премьер-министри',
    ),
  ),
  element(
    'span',
    '',
    localeText(
      'Речь на гражданской панихиде · 17 октября 2008 года',
      'Жарандык коштошуудагы сөз · 2008-жылдын 17-октябры · орусчадан редакциялык котормо',
    ),
  ),
);
const endingSourceLink = element('a', 'ep-end__source-link', '24.kg · ЦентрАзия ↗');
endingSourceLink.href = 'https://centrasia.org/newsA.php?st=1224307680';
endingSourceLink.target = '_blank';
endingSourceLink.rel = 'noopener noreferrer';
endingQuoteSource.append(endingSourceLink);
endingQuote.append(endingQuoteSource);
const back = element('a', 'ep-end__back', localeText('Вернуться к началу ↑', 'Башына кайтуу ↑'));
back.href = '#assembled-biography';
endingCopy.append(
  element('p', 'ep-eyebrow', localeText('17 октября 2008 · последнее слово', '2008-жылдын 17-октябры · акыркы сөз')),
  back,
  endingQuote,
);
ending.append(photoFigure(storyPhoto('family-09'), 'ep-end__photo'), endingCopy);

const segmentRail = createSegmentRail(segments);

const assembledNodes: Node[] = [
  intro,
  biographyPrologue,
  chapterGallery,
  segmentRail,
  ...pageChapters,
];
assembledNodes.push(ending);
root.replaceChildren(...assembledNodes);

mountMapStages(root);
mountSegmentSpy(root);
mountIdsGallery(root);


// Stories viewer: full-screen photo stories with highlights tray per chapter
// Editorial story copy: short bilingual retellings for every anchored photo slide.
// Keys are `${chapter.id}:${sectionIndex}:${photoKey}` and MUST cover every anchor
// in sectionPhotoAnchors + storyOnlyPhotoAnchors (createStorySlides throws otherwise).
const storySlideCopy: Readonly<Record<string, StorySlideCopy>> = {
  '01:2:origin-05': {
    text: localeText(
      'В восемнадцать лет Аманбек получил диплом по специальности комбайнера-механизатора и начал работать в колхозе имени Жданова.',
      'Он сегиз жашында Аманбек комбайнер-механизатор адистиги боюнча диплом алып, Жданов атындагы колхоздо иштей баштаган.',
    ),
  },
  '01:0:origin-01': {
    text: localeText(
      'Отец, Карыпкул Орозалиев, возглавлял колхоз; мать, Рысбү Исмаилова, вела хозяйство и растила детей в Кара‑Суу.',
      'Атасы Карыпкул Орозалиев колхозду жетектеген; апасы Рысбү Исмаилова Кара‑Сууда үй тиричилигин жүргүзүп, балдарды өстүргөн.',
    ),
  },
  '01:0:origin-06': {
    text: localeText(
      'По воспоминаниям близких, Карыпкул Орозалиев помогал нуждающимся, вдовам и сиротам и поддерживал способных сельских детей.',
      'Жакындарынын эскерүүсү боюнча, Карыпкул Орозалиев муктаждарга, жетим-жесирлерге жардам берип, зээндүү айыл балдарын колдогон.',
    ),
  },
  '01:0:origin-08': {
    text: localeText(
      'Рысбү Исмаилова растила детей; односельчане приглашали её участвовать в традиционных обрядах.',
      'Рысбү Исмаилова балдарды өстүргөн; айылдаштары аны салттуу жөрөлгөлөргө катышууга чакырышкан.',
    ),
  },
  '01:1:origin-02': {
    text: localeText(
      'В школьные годы Аманбек много читал, задавал вопросы, занимался борьбой и играл в волейбол.',
      'Мектеп жылдарында Аманбек көп окуп, суроо берип, күрөшкө кызыгып, волейбол ойногон.',
    ),
  },
  '01:1:origin-03': {
    text: localeText(
      'С двоюродным братом Дүйшөном и другом Аманом, 1954 год.',
      'Бөлөсү Дүйшөн жана досу Аман менен, 1954-жыл.',
    ),
  },
  '01:1:origin-04': {
    text: localeText(
      'В 1956 году — выпускной класс средней школы № 1 города Таласа.',
      '1956‑жылы — Талас шаарындагы № 1 орто мектептин бүтүрүүчү классы.',
    ),
  },
  '02:0:education-01': {
    text: localeText(
      'В 1957 году Аманбек приехал в Пржевальск — нынешний Каракол — учиться на филолога.',
      '1957‑жылы Аманбек Пржевальскиге — азыркы Караколго — орус тилин жана адабиятын окуганы келген.',
    ),
  },
  '02:0:education-02': {
    text: localeText(
      'Студенческие годы: учёба и работа в комсомольской организации факультета.',
      'Студенттик жылдар: окуу жана факультеттин комсомол уюмундагы иш.',
    ),
  },
  '02:0:education-04': {
    text: localeText(
      'На Иссык-Куле, лето 1959 года.',
      'Ысык-Көлдө, 1959‑жылдын жайы.',
    ),
  },
  '02:0:education-03': {
    text: localeText(
      'По воспоминаниям семьи, Аманбек старался хорошо учиться и активно работать в комсомоле, чтобы привлечь внимание Клары Джидеевой.',
      'Үй-бүлө мүчөлөрүнүн эскеришинче, Клара Джидееванын көңүлүн буруу үчүн Аманбек жакшы окуп, комсомол ишине жигердүү катышууга аракет кылган.',
    ),
  },
  '02:0:education-05': {
    text: localeText(
      'За отличную учёбу и общественную активность Карыпкулов получил Ленинскую стипендию.',
      'Мыкты окуусу жана коомдук жигердүүлүгү үчүн Карыпкулов Лениндик стипендия алды.',
    ),
  },
  '02:0:education-06': {
    text: localeText(
      'Общественная жизнь института: партийные собрания и комсомольская работа.',
      'Институттун коомдук турмушу: партиялык жыйындар жана комсомол иши.',
    ),
  },
  '02:0:education-07': {
    text: localeText(
      'В 1961 году на педагогической практике Аманбек впервые преподавал школьникам.',
      '1961‑жылы педагогикалык практикада Аманбек биринчи жолу мектеп окуучуларына сабак берген.',
    ),
  },
  '03:0:education-09': {
    text: localeText(
      'В 1964 году Карыпкулов поступил в аспирантуру КГУ по специальности «Философия».',
      '1964‑жылы Карыпкулов КМУнун «Философия» адистиги боюнча аспирантурасына өттү.',
    ),
  },
  '03:0:education-08': {
    text: localeText(
      'Аманбек и Клара в аспирантуре, 1964 год.',
      'Аманбек менен Клара аспирантурада, 1964-жыл.',
    ),
  },
  '04:0:state-01': {
    text: localeText(
      'В 1968 году молодого преподавателя и исследователя пригласили в отдел науки и учебных заведений ЦК.',
      '1968‑жылы жаш окутуучу жана изилдөөчүнү БКнын илим жана окуу жайлары бөлүмүнө чакырышты.',
    ),
  },
  '04:0:state-02': {
    text: localeText(
      'Перед официальной встречей, 1972 год.',
      'Расмий жолугушуунун алдында, 1972-жыл.',
    ),
  },
  '04:0:state-03': {
    text: localeText(
      'С писателями, поэтами и учёными, 1973 год.',
      'Жазуучулар, акындар жана окумуштуулар менен, 1973-жыл.',
    ),
  },
  '04:0:state-04': {
    text: localeText(
      'В 1974 году — с академиком П. Н. Федосеевым.',
      '1974‑жылы — академик П. Н. Федосеев менен.',
    ),
  },
  '04:0:state-05': {
    text: localeText(
      'С президентом Академии наук СССР А. П. Александровым.',
      'СССР Илимдер академиясынын президенти А. П. Александров менен.',
    ),
  },
  '04:0:state-06': {
    text: localeText(
      'На собрании работников образования, 1975 год.',
      'Билим берүү кызматкерлеринин жыйынында, 1975-жыл.',
    ),
  },
  '04:0:state-07': {
    text: localeText(
      'Рабочие поездки с руководителем республики Т. Усубалиевым.',
      'Республика жетекчиси Т. Усубалиев менен иш сапарлар.',
    ),
  },
  '04:0:state-08': {
    text: localeText(
      'На республиканском субботнике, 1979 год.',
      'Республикалык ишембиликте, 1979-жыл.',
    ),
  },
  '04:0:state-09': {
    text: localeText(
      'Делегаты XVII съезда Компартии Кыргызстана, 1981 год.',
      'Кыргызстан Компартиясынын XVII съездинин делегаттары, 1981‑жыл.',
    ),
  },
  '04:0:state-10': {
    text: localeText(
      'На всесоюзном совещании работников здравоохранения в Москве, 1981 год.',
      'Москвадагы саламаттык сактоо кызматкерлеринин бүткүл союздук кеңешмесинде, 1981‑жыл.',
    ),
  },
  '04:1:state-11': {
    text: localeText(
      'На первомайском параде с поэтом Аалы Токомбаевым.',
      'Биринчи май парадында акын Аалы Токомбаев менен.',
    ),
  },
  '04:1:state-12': {
    text: localeText(
      'Рабочая поездка в Монголию, 1984 год.',
      'Монголиядагы иш сапар, 1984‑жыл.',
    ),
  },
  '05:0:state-13': {
    text: localeText(
      'В 1985 году председатель Верховного Совета Карыпкулов участвовал во встрече премьер-министра Индии Раджива Ганди.',
      '1985‑жылы Жогорку Советтин төрагасы Карыпкулов Индиянын премьер-министри Раджив Гандини тосуп алууга катышкан.',
    ),
  },
  '07:0:transition-01': {
    text: localeText(
      'Талас готовился к 1000‑летию «Манаса»: стройка, протокол, транспорт и календарь юбилейных мероприятий.',
      'Талас «Манас» эпосунун 1000 жылдыгына даярданган: курулуш, протокол, транспорт жана мааракелик иш-чаралардын календары.',
    ),
  },
  '08:0:transition-01': {
    text: localeText(
      'Талас, 1995 год: подготовка площадки к тысячелетию эпоса «Манас».',
      'Талас, 1995-жыл: «Манас» эпосунун миң жылдыгына арналган аянтты даярдоо.',
    ),
  },
  '08:0:transition-02': {
    text: localeText(
      'Награда Аманбеку Карыпкулову за вклад в организацию «Манас-1000».',
      'Аманбек Карыпкуловго «Манас-1000» программасын уюштурууга кошкон салымы үчүн берилген сыйлык.',
    ),
  },
  '08:0:manas-event-01': {
    text: localeText(
      'Талас, 1995 год: конное шествие на главной площадке юбилея «Манас-1000».',
      'Талас, 1995-жыл: «Манас-1000» мааракесинин башкы аянтындагы атчандар жүрүшү.',
    ),
  },
  '08:0:manas-event-02': {
    text: localeText(
      'Флаг Кыргызстана над участниками массового праздничного представления.',
      'Массалык майрамдык көрсөтүүнүн катышуучуларынын үстүндөгү Кыргызстандын желеги.',
    ),
  },
  '08:0:manas-event-03': {
    text: localeText(
      'Тысячи участников стали частью театрализованной программы в Таласе.',
      'Миңдеген катышуучу Таластагы театрлаштырылган программанын бир бөлүгү болгон.',
    ),
  },
  '09:0:broadcast-01': {
    text: localeText(
      'При Карыпкулове для КТР закупили в Иране современное телевизионное оборудование.',
      'Карыпкуловдун тушунда МТРК үчүн Ирандан заманбап телевизиондук жабдуу сатылып алынды.',
    ),
  },
  '09:0:broadcast-02': {
    text: localeText(
      'Чынгыз Айтматов на Кыргызском телевидении, 1998 год.',
      'Чыңгыз Айтматов Кыргыз телекөрсөтүүсүндө, 1998-жыл.',
    ),
  },
  '09:0:broadcast-03': {
    text: localeText(
      'Рабочий кабинет руководителя ГТРК: эфирное время увеличилось с 4–5 до 17 часов в сутки.',
      'МТРК жетекчисинин иш бөлмөсү: эфир убактысы суткасына 4–5 сааттан 17 саатка чейин көбөйдү.',
    ),
  },
  '09:0:broadcast-04': {
    text: localeText(
      'Международные связи телерадио: соглашение с делегацией КНР.',
      'Телерадионун эл аралык байланыштары: КЭР делегациясы менен келишим.',
    ),
  },
  '09:0:culture-01': {
    text: localeText(
      'Нооруз на площади Ала-Тоо, 1998 год.',
      'Ала-Тоо аянтындагы Нооруз, 1998-жыл.',
    ),
  },
  '09:0:culture-02': {
    text: localeText(
      'С академиком А. Александровым, 1998 год.',
      'Академик А. Александров менен, 1998‑жыл.',
    ),
  },
  '09:0:culture-03': {
    text: localeText(
      'На 75‑летии маэстро А. Жумахматова.',
      'Маэстро А. Жумахматовдун 75 жылдык мааракесинде.',
    ),
  },
  '09:0:culture-04': {
    text: localeText(
      'Встреча с Т. Усубалиевым, 1998 год.',
      'Т. Усубалиев менен жолугушуу, 1998‑жыл.',
    ),
  },
  '09:1:culture-05': {
    text: localeText(
      'На встрече Чынгыза Айтматова с учёными.',
      'Чыңгыз Айтматовдун окумуштуулар менен жолугушуусунда.',
    ),
  },
  '09:1:culture-06': {
    text: localeText(
      'В 1998 году Карыпкулов стал лауреатом Государственной премии в области науки и техники.',
      '1998‑жылы Карыпкулов илим жана техника жаатындагы Мамлекеттик сыйлыктын лауреаты болду.',
    ),
  },
  '10:0:diplomacy-01': {
    text: localeText(
      'В 2001 году Карыпкулов возглавил Администрацию президента Кыргызстана.',
      '2001‑жылы Карыпкулов Кыргызстандын Президентинин Администрациясын жетектеди.',
    ),
  },
  '10:0:diplomacy-02': {
    text: localeText(
      'В 2001 году Карыпкулов возглавил Администрацию президента Кыргызской Республики.',
      '2001‑жылы Карыпкулов Кыргыз Республикасынын Президентинин Администрациясын жетектеди.',
    ),
  },
  '11:0:diplomacy-03': {
    text: localeText(
      'С министром иностранных дел Аскаром Айтматовым, 2003 год.',
      'Тышкы иштер министри Аскар Айтматов менен, 2003-жыл.',
    ),
  },
  '11:0:diplomacy-04': {
    text: localeText(
      'Переговоры с Реджепом Тайипом Эрдоганом, Анкара, 2003 год.',
      'Режеп Тайип Эрдоган менен сүйлөшүү, Анкара, 2003‑жыл.',
    ),
  },
  '11:0:diplomacy-06': {
    text: localeText(
      'Вручение верительных грамот президенту Турции Ахмету Недждету Сезеру.',
      'Түркиянын президенти Ахмет Неждет Сезерге ишеним грамоталарын тапшыруу.',
    ),
  },
  '11:0:diplomacy-07': {
    text: localeText(
      'С бывшим президентом Турции Сулейманом Демирелем.',
      'Түркиянын мурдагы президенти Сүлейман Демирел менен.',
    ),
  },
  '11:0:diplomacy-05': {
    text: localeText(
      'В 2003 году в Стамбуле вышла книга Карыпкулова «КТР: Испытание временем».',
      '2003‑жылы Стамбулда Карыпкуловдун «КТР: Испытание временем» китеби чыкты.',
    ),
  },
  '11:0:diplomacy-08': {
    text: localeText(
      'С Реджепом Тайипом Эрдоганом, Анкара, 2004 год.',
      'Режеп Тайип Эрдоган менен, Анкара, 2004‑жыл.',
    ),
  },
  '12:3:family-01': {
    text: localeText(
      'Семья Аманбека Карыпкулова.',
      'Аманбек Карыпкуловдун үй-бүлөсү.',
    ),
  },
  '12:0:family-02': {
    text: localeText(
      'Аманбек и Клара воспитали троих детей — Элеонору, Нурлана и Асель.',
      'Аманбек менен Клара Элеонора, Нурлан жана Асел аттуу үч баланы тарбиялап өстүрүштү.',
    ),
  },
  '12:0:family-03': {
    text: localeText(
      'Аманбек и Клара на мероприятии.',
      'Аманбек менен Клара иш-чарада.',
    ),
  },
  '12:3:family-04': {
    text: localeText(
      'Аманбек с Кларой, сыном Нурланом и внуком Семетеем.',
      'Аманбек Клара, уулу Нурлан жана небереси Семетей менен.',
    ),
  },
  '12:0:family-05': {
    text: localeText(
      'Клара Хусаиновна Джидеева — профессор, филолог, литературовед, педагог и исследователь творчества Пушкина.',
      'Клара Хусаиновна Джидеева — профессор, филолог, адабиятчы, педагог жана Пушкиндин чыгармачылыгын изилдөөчү.',
    ),
  },
  '12:3:family-06': {
    text: localeText(
      'Правнуки Аманбека Карыпкулова.',
      'Аманбек Карыпкуловдун чөбөрөлөрү.',
    ),
  },
  '12:3:family-08': {
    text: localeText(
      'Аманбек Карыпкулов с семьёй.',
      'Аманбек Карыпкулов үй-бүлөсү менен.',
    ),
  },
  '12:2:school-film-022': {
    text: localeText(
      'Дальний план школы имени Аманбека Карыпкулова.',
      'Аманбек Карыпкулов атындагы мектептин алыстан көрүнүшү.',
    ),
  },
  '12:1:school-film-032': {
    text: localeText(
      'В школе открыт музей Аманбека Карыпкулова.',
      'Мектепте Аманбек Карыпкуловдун музейи ачылган.',
    ),
  },
  '12:1:school-film-025': {
    text: localeText(
      'В 2025 году школу впервые со времени постройки капитально отремонтировали.',
      '2025‑жылы мектеп курулгандан бери биринчи жолу капиталдык оңдоодон өттү.',
    ),
  },
  '12:1:school-film-021': {
    text: localeText(
      'Учительница ведёт урок в обновлённом классе школы имени Аманбека Карыпкулова.',
      'Мугалим Аманбек Карыпкулов атындагы мектептин жаңыланган классында сабак өтүп жатат.',
    ),
  },
  '12:2:school-film-031': {
    text: localeText(
      'Учебный кабинет школы имени Аманбека Карыпкулова.',
      'Аманбек Карыпкулов атындагы мектептин окуу кабинети.',
    ),
  },
  '12:1:school-film-030': {
    text: localeText(
      'В классах школы имени Карыпкулова каждый день идут обычные уроки.',
      'Карыпкулов атындагы мектептин класстарында күн сайын кадимки сабактар өтөт.',
    ),
  },
  '12:3:family-09': {
    eyebrow: localeText('Послесловие', 'Соңку сөз'),
    title: localeText('Наследие, которое остаётся', 'Өчпөс мурас'),
    text: localeText(
      'Аманбек Карыпкулов с сыном Нурланом. Его наследие живёт в книгах, в «Манасе», в школе его имени и прежде всего — в делах, совершённых для Кыргызстана.',
      'Аманбек Карыпкулов уулу Нурлан менен. Анын мурасы китептерде, «Манаста», өзүнүн ысымындагы мектепте жана баарынан мурда Кыргызстан үчүн жасаган иштеринде жашайт.',
    ),
    autoAdvance: false,
  },
};

const storyEditorialSlides: Readonly<Partial<Record<string, readonly StorySlideSpec[]>>> = {
  '06': [
    {
      format: 'folio',
      mark: 'II',
      eyebrow: localeText('Бишкек · 1995', 'Бишкек · 1995-жыл'),
      title: '«Манас-энциклопедия»',
      text: localeText(
        'В выходных данных двухтомника Аманбек Карыпкулов и Р. Сарыбеков указаны редакторами.',
        'Эки томдуктун чыгарылыш маалыматында Аманбек Карыпкулов менен Р. Сарыбеков редакторлор катары көрсөтүлгөн.',
      ),
      artwork: [
        {
          src: './photos/public/books/manas-encyclopedia-tom-1.jpg',
          alt: localeText('Обложка первого тома «Манас-энциклопедии».', '«Манас-энциклопедиянын» биринчи томунун мукабасы.'),
          label: localeText('Том I', 'I том'),
          width: 222,
          height: 300,
        },
        {
          src: './photos/public/books/manas-encyclopedia-tom-2.jpg',
          alt: localeText('Обложка второго тома «Манас-энциклопедии».', '«Манас-энциклопедиянын» экинчи томунун мукабасы.'),
          label: localeText('Том II', 'II том'),
          width: 218,
          height: 300,
        },
      ],
      links: [
        {
          label: localeText('Том I', 'I том'),
          href: 'https://bilim.akipress.org/lib/book:466/manas-entsiklopediya-tom-1-bishkek-1995g/',
        },
        {
          label: localeText('Том II', 'II том'),
          href: 'https://bilim.akipress.org/lib/book:464/manas-entsiklopediya-tom-2-bishkek-1995g/',
        },
      ],
      autoAdvance: false,
    },
    {
      format: 'facts',
      mark: '1994',
      eyebrow: localeText('Региональная энциклопедия', 'Аймактык энциклопедия'),
      title: '«Чүй облусу / Чуйская область»',
      text: localeText(
        'Библиографическая запись связывает двуязычное издание с Аманбеком Карыпкуловым.',
        'Библиографиялык жазма кош тилдүү басылманы Аманбек Карыпкулов менен байланыштырат.',
      ),
      facts: localeText(
        [
          { label: 'Объём', value: '718 страниц', note: 'Энциклопедия и биографический словарь региона.' },
          { label: 'Языки', value: 'Кыргызский · русский', note: 'Двуязычное издание Кыргызской энциклопедии.' },
        ],
        [
          { label: 'Көлөмү', value: '718 бет', note: 'Аймактын энциклопедиясы жана биографиялык сөздүгү.' },
          { label: 'Тилдери', value: 'Кыргызча · орусча', note: 'Кыргыз энциклопедиясынын кош тилдүү басылмасы.' },
        ],
      ),
      links: [{ label: 'Open Library', href: 'https://openlibrary.org/works/OL23589261W' }],
      autoAdvance: false,
    },
  ],
  '08': [
    {
      format: 'facts',
      mark: '1995',
      eyebrow: localeText('«Манас-1000»', '«Манас-1000»'),
      title: localeText('Масштаб культурной программы', 'Маданий программанын масштабы'),
      text: localeText(
        'Книги, фильмы, международный симпозиум и участники со всего мира.',
        'Китептер, тасмалар, эл аралык симпозиум жана дүйнөнүн ар тарабынан келген катышуучулар.',
      ),
      facts: localeText(
        [
          { label: 'Издания', value: 'Более 50', note: 'Книги, брошюры, пособия и буклеты.' },
          { label: 'Экран', value: 'Более 10 фильмов', note: 'Кино- и видеоработы юбилейной программы.' },
          { label: 'Симпозиум', value: 'Более 130 учёных', note: '«Манас и мировое эпическое наследие».' },
          { label: 'География', value: 'Более 60 стран', note: 'Участники мероприятий юбилейного года.' },
        ],
        [
          { label: 'Басылмалар', value: '50дөн ашык', note: 'Китептер, брошюралар, окуу куралдары жана буклеттер.' },
          { label: 'Экран', value: '10дон ашык тасма', note: 'Мааракелик программанын кино жана видео иштери.' },
          { label: 'Симпозиум', value: '130дан ашык окумуштуу', note: '«Манас жана дүйнөлүк эпикалык мурас».' },
          { label: 'География', value: '60тан ашык өлкө', note: 'Маараке жылындагы иш-чаралардын катышуучулары.' },
        ],
      ),
      autoAdvance: false,
    },
  ],
};

const storiesGroups: StoriesGroup[] = longformChapters.map((chapter) => {
  const coverKey = segmentPhotos[chapter.id];
  if (!coverKey) throw new Error(`Missing story cover photograph for chapter ${chapter.id}`);
  const coverPhoto = storyPhoto(coverKey);
  const storyAnchors: StoryPhotoAnchor[] = [];
  const storyCopyByAnchor: Record<string, StorySlideCopy> = {};

  for (const [sectionIndex] of chapter.sections.entries()) {
    const sectionKey = `${chapter.id}:${sectionIndex}`;
    const photoAnchors = sectionPhotoAnchors[sectionKey] ?? storyOnlyPhotoAnchors[sectionKey];
    if (!photoAnchors?.length) continue;
    for (const [key] of photoAnchors) {
      const id = `${sectionKey}:${key}`;
      const photo = storyPhoto(key);
      storyAnchors.push({ id, sectionIndex, photo });
      const copy = storySlideCopy[id];
      if (!copy) {
        throw new Error(
          `Missing bilingual story copy for anchor ${id} — add it to storySlideCopy in assembled.ts`,
        );
      }
      storyCopyByAnchor[id] = copy;
    }
  }

  const slides = createStorySlides(chapter, storyAnchors, coverPhoto, {
    slides: storyCopyByAnchor,
  });
  const editorialSlides = storyEditorialSlides[chapter.id];
  if (editorialSlides?.length) slides.splice(1, 0, ...editorialSlides);

  return {
    id: chapter.id,
    title: chapter.label,
    coverPhoto,
    slides,
  };
});

const storiesViewer = createStoriesViewer(storiesGroups, {
  onExit: (group) => {
    const targetId = `chapter-${group.id}`;
    const target = document.getElementById(targetId);
    if (!target) return;
    const focusLine = window.innerHeight * 0.32;
    const targetRect = target.getBoundingClientRect();
    if (targetRect.top <= focusLine && targetRect.bottom > focusLine) return;
    segmentRail
      .querySelector<HTMLAnchorElement>(`[data-segment-target="${targetId}"]`)
      ?.click();
  },
});

if (storiesViewer) {
  document.body.append(storiesViewer.element);
  mountContextualStories({
    nav: segmentRail,
    groups: storiesGroups,
    viewer: storiesViewer,
  });


  // Global trigger button in intro
  const storiesTrigger = document.createElement('button');
  storiesTrigger.type = 'button';
  storiesTrigger.className = 'stories-trigger ep-intro__action ep-intro__action--stories';
  storiesTrigger.innerHTML = '<span>' + localeText('Смотреть как сториз', 'Тарыхча катары көрүү') + '</span><span class="ep-intro__action-icon" aria-hidden="true"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="10" cy="10" r="7.5"/><circle cx="10" cy="10" r="3.5" fill="currentColor" stroke="none"/></svg></span>';
  storiesTrigger.setAttribute('aria-label', localeText('Открыть биографию в формате сториз', 'Өмүр баянды тарыхча форматында ачуу'));
  storiesTrigger.addEventListener('click', () => {
    storiesViewer.show({ startGroupIndex: 0, returnFocusTo: storiesTrigger });
  });
  introActions.append(storiesTrigger);
}
